// AI Query 결과(columns/rows) → chart.js 스펙 자동 생성(규칙 기반, LLM 미사용).
// 집계형(차원 1 + 수치 측정값)만 차트로. 스칼라/상세형/과다행은 null 반환(표만 표시).
// chart.js 2.9 + vue-chartjs 3.x 옵션 규격.

// herp 전역 팔레트($colorListForChart)와 동일.
const PALETTE = ['#4466c7', '#47a5fc', '#169e94', '#8bc743', '#faa306', '#f23551', '#cd2330', '#910d3e', '#0c4266', '#135e71'];

const TIME_COL = /^(ym|month|period|yyyymm|year|date)$/i;
const ID_COL = /(_id|_no|id|no)$/i; // 측정값에서 제외할 식별자성 컬럼

function isNumeric(v) {
	return v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
}

/**
 * @returns {null | { type, data, options }} chart.js config, 또는 차트 부적합 시 null
 */
export function buildChartSpec(columns, rows) {
	if (!Array.isArray(columns) || !Array.isArray(rows)) return null;
	if (rows.length < 2) return null; // 스칼라/단일값은 표로 충분
	if (columns.length > 4) return null; // 컬럼 많으면 상세 테이블 → 차트 부적합

	// 컬럼 분류: 전부(널 제외) 숫자면 측정값 후보, 아니면 라벨(차원)
	const numericCols = columns.filter(c => rows.every(r => r[c] === null || isNumeric(r[c])) && rows.some(r => isNumeric(r[c])));
	const labelCols = columns.filter(c => !numericCols.includes(c));

	// 측정값 = 숫자 컬럼 중 식별자성(_id/_no) 제외
	const measures = numericCols.filter(c => !ID_COL.test(c));
	if (measures.length === 0 || labelCols.length === 0) return null;

	// 라벨(차원) 선택: 시간형 우선 → *_name → *_code → 첫 라벨
	let labelCol = labelCols.find(c => TIME_COL.test(c));
	const isTime = !!labelCol;
	if (!labelCol) labelCol = labelCols.find(c => /name$/i.test(c)) || labelCols.find(c => /code$/i.test(c)) || labelCols[0];

	// 카테고리(비시간) 막대가 너무 많으면 표가 나음
	if (!isTime && rows.length > 40) return null;

	const labels = rows.map(r => String(r[labelCol] === null || r[labelCol] === undefined ? '' : r[labelCol]));
	const type = isTime ? 'line' : 'bar';
	const useMeasures = measures.slice(0, 4);
	const singleBar = type === 'bar' && useMeasures.length === 1;

	const datasets = useMeasures.map((m, i) => {
		const data = rows.map(r => (r[m] === null || r[m] === '' || r[m] === undefined ? null : Number(r[m])));
		if (type === 'line') {
			const color = PALETTE[i % PALETTE.length];
			return { label: m, data, borderColor: color, pointBackgroundColor: color, backgroundColor: color, fill: false };
		}
		// bar: 단일 측정값이면 카테고리별 색, 다중이면 측정값별 색
		return {
			label: m,
			data,
			backgroundColor: singleBar ? labels.map((_, k) => PALETTE[k % PALETTE.length]) : PALETTE[i % PALETTE.length],
		};
	});

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		legend: { display: useMeasures.length > 1 },
		tooltips: { mode: type === 'line' ? 'index' : 'nearest', intersect: type !== 'line' },
		scales: {
			yAxes: [{ ticks: { beginAtZero: true, callback: v => commaShort(v) } }],
			xAxes: [{ ticks: { maxRotation: 45, minRotation: 0, autoSkip: true, maxTicksLimit: 24 } }],
		},
		elements: { line: { tension: 0, borderWidth: 2, fill: false } },
		plugins: { datalabels: { display: false } }, // 전역 datalabels 플러그인 비활성(과밀 방지)
	};

	return { type, data: { labels, datasets }, options };
}

// 축 눈금 축약(1200000 → 1.2M)
function commaShort(v) {
	const n = Number(v);
	if (!isFinite(n)) return v;
	const a = Math.abs(n);
	if (a >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
	if (a >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
	if (a >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
	return String(n);
}
